import { useCallback } from 'react';
import { useUpdateGoalMutation } from 'shared/hooks/queries/use-update-goal-mutation';
import { useUpdateTasksMutation } from 'shared/hooks/queries/use-update-tasks-mutation';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import {
  ActionEvents,
  taskTypeToTrackingTaskType,
} from 'shared/services/tracking';
import { ID } from 'shared/types/id';
import { Task, UpdateTask } from 'shared/types/task';
import { TaskActionType } from 'shared/types/task-action';

export const useRestoreTasks = () => {
  const track = useTrackEvents();
  const { mutate: updateTasks } = useUpdateTasksMutation();
  const { mutate: updateGoal } = useUpdateGoalMutation();

  return useCallback(
    async (tasks: Task[]) => {
      const deletedTasks = tasks.filter((t) => !!t.deletedAt);

      if (!deletedTasks.length) {
        return;
      }

      // Restore the tasks
      const updates: UpdateTask[] = deletedTasks.map((task) => ({
        id: task.id,
        deletedAt: null,
      }));

      updateTasks(updates);

      // Track events
      deletedTasks.forEach((task) => {
        track(ActionEvents.TaskRestore, {
          type: taskTypeToTrackingTaskType[task.type],
        });
      });

      // Update related goals
      const taskRelatedGoalIds = deletedTasks.reduce<
        Record<ID, { taskCount: number; completedTaskCount: number }>
      >((acc, task) => {
        // Only update for non-timeslot goals to match useDeleteTask/useRestoreTask single logic
        if (task.goalId && task.action?.type !== TaskActionType.GoalTimeslot) {
          acc[task.goalId] = acc[task.goalId] ?? {
            taskCount: 0,
            completedTaskCount: 0,
          };
          acc[task.goalId].taskCount += 1;

          if (task.completedAt) {
            acc[task.goalId].completedTaskCount += 1;
          }
        }

        return acc;
      }, {});

      Object.entries(taskRelatedGoalIds).forEach(
        ([goalId, { taskCount, completedTaskCount }]) => {
          updateGoal({
            goal: { id: goalId, taskCount, completedTaskCount },
          });
        },
      );
    },
    [updateGoal, updateTasks, track],
  );
};
